// View 1 — KB list page.
//
// Per design/UI : UX v1.0/for-engineering-kb/specs/kb-strings.md.
// Layout: PageHeader → stat cards → filter row → table → footer count.
// Mirrors Tenants 1:1 (chips, stat-cards, marketing empty state, RBAC).
//
// V1.0 scope:
//   - Status chips + type/domain/language dropdowns + search
//   - 4 stat cards (ENTRIES / PUBLISHED / IN REVIEW / DRAFT; click filters)
//   - "+ New entry" → navigates to /admin/knowledge/base/new (full-page editor per designer)
//   - Row click → /admin/knowledge/base/:id
//   - RBAC: super_admin + content_editor only (filtered out by nav model via kb:read)

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  StatCard,
  TextInput,
} from '@gc-coach/design-system';
import type {
  Domain,
  KBEntryLanguage,
  KBEntryStatusFilter,
  KBEntrySummary,
  KBEntryType,
} from '@gc-coach/shared';
import { ALL_DOMAINS, ALL_KB_ENTRY_TYPES } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { formatRelativeTime } from '../users/time.js';
import { useKBEntries } from './useKBEntries.js';
import { duplicateKBEntry } from '../api/endpoints.js';
import styles from './KBListPage.module.css';

const LANGUAGES: ReadonlyArray<KBEntryLanguage> = ['en', 'fr'];

export function KBListPage() {
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const [statusFilter, setStatusFilter] = useState<KBEntryStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<KBEntryType | ''>('');
  const [domainFilter, setDomainFilter] = useState<Domain | ''>('');
  const [langFilter, setLangFilter] = useState<KBEntryLanguage | ''>('');
  const [q, setQ] = useState('');

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(domainFilter ? { domain: domainFilter } : {}),
      ...(langFilter ? { language: langFilter } : {}),
      ...(q.trim() ? { q } : {}),
    }),
    [statusFilter, typeFilter, domainFilter, langFilter, q],
  );

  const { data, isLoading } = useKBEntries(query);
  const entries = useMemo(() => data?.entries ?? [], [data]);
  const counts = data?.counts ?? {
    total: 0,
    draft: 0,
    in_review: 0,
    published: 0,
    archived: 0,
    removed: 0,
  };

  // Marketing-style empty state only when truly zero entries AND no filters
  // applied. Filtered-empty falls back to the in-table empty state so the
  // user understands their filter is restrictive.
  const showMarketingEmpty =
    !isLoading &&
    counts.total === 0 &&
    statusFilter === 'all' &&
    !typeFilter &&
    !domainFilter &&
    !langFilter &&
    !q.trim();

  const subText = tt('kb.subTemplate', {
    total: counts.total,
    in_review: counts.in_review,
    draft: counts.draft,
  });

  return (
    <AdminShell
      activeKey="kb"
      breadcrumb={[t('kb.breadcrumbKnowledge'), t('kb.breadcrumbBase')]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('kb.title')}
        sub={subText}
        actions={
          <Button kind="primary" onClick={() => navigate('/admin/knowledge/base/new')}>
            {t('kb.newEntryButton')}
          </Button>
        }
      />

      {showMarketingEmpty ? (
        <div className={styles.emptyMarketing}>
          <h2>{t('kb.emptyMarketingTitle')}</h2>
          <p>{t('kb.emptyMarketingBody')}</p>
          <Button kind="primary" onClick={() => navigate('/admin/knowledge/base/new')}>
            {t('kb.newEntryButton')}
          </Button>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className={styles.statRow}>
            <StatCard
              label={t('kb.statEntries')}
              value={counts.total.toString()}
              onClick={() => setStatusFilter('all')}
            />
            <StatCard
              label={t('kb.statPublished')}
              value={counts.published.toString()}
              onClick={() => setStatusFilter('published')}
            />
            <StatCard
              label={t('kb.statInReview')}
              value={counts.in_review.toString()}
              onClick={() => setStatusFilter('in_review')}
            />
            <StatCard
              label={t('kb.statDraft')}
              value={counts.draft.toString()}
              onClick={() => setStatusFilter('draft')}
            />
          </div>

          {/* Filters */}
          <div className={styles.filterRow}>
            <div className={styles.chips} role="group" aria-label="Status filter">
              <FilterChip
                label={tt('kb.chipAllTemplate', { n: counts.total - counts.removed })}
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              />
              <FilterChip
                label={tt('kb.chipPublishedTemplate', { n: counts.published })}
                active={statusFilter === 'published'}
                onClick={() => setStatusFilter('published')}
              />
              <FilterChip
                label={tt('kb.chipInReviewTemplate', { n: counts.in_review })}
                active={statusFilter === 'in_review'}
                onClick={() => setStatusFilter('in_review')}
              />
              <FilterChip
                label={tt('kb.chipDraftTemplate', { n: counts.draft })}
                active={statusFilter === 'draft'}
                onClick={() => setStatusFilter('draft')}
              />
              <FilterChip
                label={tt('kb.chipArchivedTemplate', { n: counts.archived })}
                active={statusFilter === 'archived'}
                onClick={() => setStatusFilter('archived')}
              />
              <FilterChip
                label={tt('kb.chipRemovedTemplate', { n: counts.removed })}
                active={statusFilter === 'removed'}
                onClick={() => setStatusFilter('removed')}
              />
            </div>

            <div className={styles.filterRight}>
              <label className={styles.typeFilter}>
                <span className={styles.typeFilterLabel}>{t('kb.typeFilterLabel')}</span>
                <select
                  className={styles.roleSelect}
                  value={typeFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setTypeFilter(e.target.value as KBEntryType | '')
                  }
                >
                  <option value="">{t('kb.typeFilterAll')}</option>
                  {ALL_KB_ENTRY_TYPES.map((tp) => (
                    <option key={tp} value={tp}>
                      {typeLabel(tp, t)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.domainFilter}>
                <span className={styles.domainFilterLabel}>{t('kb.domainFilterLabel')}</span>
                <select
                  className={styles.roleSelect}
                  value={domainFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setDomainFilter(e.target.value as Domain | '')
                  }
                >
                  <option value="">{t('kb.domainFilterAll')}</option>
                  {ALL_DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {domainLabel(d, t)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.langFilter}>
                <span className={styles.langFilterLabel}>{t('kb.langFilterLabel')}</span>
                <select
                  className={styles.roleSelect}
                  value={langFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setLangFilter(e.target.value as KBEntryLanguage | '')
                  }
                >
                  <option value="">{t('kb.langFilterAll')}</option>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {l.toUpperCase()}
                    </option>
                  ))}
                </select>
              </label>

              <div className={styles.search}>
                <TextInput
                  type="search"
                  placeholder={t('kb.searchPlaceholder')}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label={t('kb.searchPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('kb.colTitle')}</th>
                  <th>{t('kb.colType')}</th>
                  <th>{t('kb.colDomain')}</th>
                  <th>{t('kb.colContributor')}</th>
                  <th>{t('kb.colVersion')}</th>
                  <th>{t('kb.colUpdated')}</th>
                  <th>{t('kb.colStatus')}</th>
                  {/* 8th col: row-action kebab (currently Duplicate on
                    * case_study only). Header empty to stay unobtrusive. */}
                  <th aria-label={t('kb.colActions')} />
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      <strong>{t('kb.emptyTitle')}</strong>
                      <span>{t('kb.emptyBody')}</span>
                    </td>
                  </tr>
                )}
                {entries.map((entry) => (
                  <KBRow
                    key={entry.id}
                    entry={entry}
                    onClick={() => navigate(`/admin/knowledge/base/${entry.id}`)}
                    onDuplicated={(newId) =>
                      navigate(`/admin/knowledge/base/${newId}`)
                    }
                  />
                ))}
              </tbody>
            </table>
            <footer className={styles.tableFooter}>
              {tt('kb.footerCountTemplate', { n: entries.length })}
            </footer>
          </div>
        </>
      )}
    </AdminShell>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────

function KBRow({
  entry,
  onClick,
  onDuplicated,
}: {
  entry: KBEntrySummary;
  onClick: () => void;
  /** Fired after a successful duplicate; parent navigates to the new
   *  entry (default behaviour: open its editor). */
  onDuplicated: (newId: string) => void;
}) {
  const { t } = useI18n();
  return (
    <tr className={styles.row} onClick={onClick} tabIndex={0}>
      <td>
        <div className={styles.cellTitle}>
          <span className={styles.titleText}>{entry.title || t('kb.untitled')}</span>
          <span className={styles.langChip}>{entry.language.toUpperCase()}</span>
        </div>
      </td>
      <td>
        <TypePill type={entry.type} />
      </td>
      <td>
        <span className={styles.domainBadge}>{shortDomain(entry.domain)}</span>
      </td>
      <td className={styles.cellMuted}>{entry.contributor}</td>
      <td>
        <span className={styles.versionMono}>v{entry.version}</span>
      </td>
      <td className={styles.cellMuted}>{formatRelativeTime(entry.updatedAt)}</td>
      <td className={statusClass(entry.status, styles)}>{statusText(entry.status, t)}</td>
      <td className={styles.cellActions}>
        {/* V1: Duplicate on case_study rows only (per Olivier 2026-10-22). */}
        {entry.type === 'case_study' && entry.status !== 'removed' && (
          <KBRowKebab entryId={entry.id} onDuplicated={onDuplicated} />
        )}
      </td>
    </tr>
  );
}

/** Row-level actions kebab (V1: a single Duplicate item). Click toggles
 *  a tiny popover; outside click or Esc closes it. Row click
 *  propagation is stopped so opening the menu doesn't accidentally
 *  navigate to the detail page. */
function KBRowKebab({
  entryId,
  onDuplicated,
}: {
  entryId: string;
  onDuplicated: (newId: string) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onDuplicateClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (pending) return;
    setPending(true);
    const result = await duplicateKBEntry(entryId);
    setPending(false);
    setOpen(false);
    if (result.ok) onDuplicated(result.data.entry.id);
  };

  return (
    <div
      ref={containerRef}
      className={styles.kebabContainer}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={styles.kebabButton}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={t('kb.rowKebabAria')}
        aria-expanded={open ? 'true' : 'false'}
        aria-haspopup="menu"
      >
        ⋮
      </button>
      {open && (
        <ul className={styles.kebabMenu} role="menu">
          <li>
            <button
              type="button"
              className={styles.kebabMenuItem}
              onClick={(e) => void onDuplicateClick(e)}
              disabled={pending}
              role="menuitem"
            >
              {pending ? t('kb.rowKebabDuplicating') : t('kb.rowKebabDuplicate')}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function TypePill({ type }: { type: KBEntryType }) {
  const { t } = useI18n();
  const cls =
    type === 'qa_pair'
      ? styles.typeQaPair
      : type === 'framework'
        ? styles.typeFramework
        : type === 'case_study'
          ? styles.typeCaseStudy
          : styles.typeExcerpt;
  return <span className={`${styles.typePill} ${cls ?? ''}`}>{typeLabel(type, t)}</span>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function typeLabel(t: KBEntryType, i18n: (k: string) => string): string {
  if (t === 'qa_pair') return i18n('kb.typeQaPair');
  if (t === 'framework') return i18n('kb.typeFramework');
  if (t === 'case_study') return i18n('kb.typeCaseStudy');
  return i18n('kb.typeExcerpt');
}

function domainLabel(d: Domain, i18n: (k: string) => string): string {
  // i18n keys mirror the domain enum keys directly.
  return i18n(`kb.domain.${d}`);
}

/** Short form for table cell — "D1" instead of the full domain key. */
function shortDomain(d: Domain): string {
  // "D1_strategy_org" → "D1"
  return d.split('_')[0] ?? d;
}

function statusClass(s: KBEntrySummary['status'], cls: Record<string, string>): string {
  if (s === 'draft') return cls['statusDraft'] ?? '';
  if (s === 'in_review') return cls['statusInReview'] ?? '';
  if (s === 'published') return cls['statusPublished'] ?? '';
  if (s === 'archived') return cls['statusArchived'] ?? '';
  return cls['statusRemoved'] ?? '';
}

function statusText(s: KBEntrySummary['status'], t: (k: string) => string): string {
  if (s === 'draft') return t('kb.statusDraft');
  if (s === 'in_review') return t('kb.statusInReview');
  if (s === 'published') return t('kb.statusPublished');
  if (s === 'archived') return t('kb.statusArchived');
  return t('kb.statusRemoved');
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles.chip, active ? styles.chipActive : null].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
